import { useEffect, useState } from 'react';
import { Building2, Plus, Edit2, MoreVertical, DollarSign, Activity, Settings, FileText, ExternalLink, Sliders } from 'lucide-react';
import { TenantLogo } from '../components/tenants/TenantLogo';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { DropdownMenu } from '../components/ui/DropdownMenu';
import { apiClient } from '../lib/api-client';
import { getUtcLastInstantOfCalendarMonth, getUtcMonthToDateRange } from '../lib/utils';
import { getTenantColor } from '../lib/colors';
import { CostReportDialog } from '../components/CostReportDialog';
import { TenantEditDialog } from '../components/tenants/TenantEditDialog';
import { AddTenantDialog } from '../components/tenants/AddTenantDialog';
import { VaultSecretDialog } from '../components/tenants/VaultSecretDialog';
import { MicrosoftGraphDomainsDialog } from '../components/tenants/MicrosoftGraphDomainsDialog';
import { DefaultParamsDialog } from '../components/tenants/DefaultParamsDialog';
import { useAuth } from '../contexts/AuthContext';
import { Skeleton } from '../components/ui/Skeleton';
import { usePermission } from '../hooks/usePermission';
import { useActivityHeartbeat } from '../hooks/useActivityHeartbeat';
import { useCacheEvents } from '../hooks/useCacheEvents';

interface Tenant {
  id: string;
  code: string;
  name: string;
  description: string | null;
  color?: string | null;
  logoUrl?: string | null;
  domains?: string[];
  validation_status?: 'valid' | 'invalid';
  validation_errors?: string | null;
  created_at: number;
  updated_at: number;
}

interface TenantCost {
  costMonthToDate: number;
  costProjectedMonthEnd: number;
}

export function Tenants() {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDesktop, setIsDesktop] = useState<boolean>(true);
  const [tenantCosts, setTenantCosts] = useState<Record<string, TenantCost>>({});
  const [costReportTenant, setCostReportTenant] = useState<{ code: string; name: string } | null>(null);
  const [editTenantCode, setEditTenantCode] = useState<string | null>(null);
  const [editingMaster, setEditingMaster] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showVaultDialog, setShowVaultDialog] = useState(false);
  const [showGraphDomainsDialog, setShowGraphDomainsDialog] = useState(false);
  const [topMenuOpen, setTopMenuOpen] = useState(false);
  const { user } = useAuth();
  const { isAllowed: canViewCosts } = usePermission('tenants', 'costs');
  const { isAllowed: canEditConfiguration } = usePermission('tenants', 'edit_configuration');
  const { isAllowed: canHealthReport } = usePermission('tenants', 'health_report');
  const { isAllowed: canAddTenant } = usePermission('tenants', 'add_tenant');
  const { isAllowed: canOpenRancherUi } = usePermission('tenants', 'open_rancher_ui');
  const [rancherUiUrl, setRancherUiUrl] = useState<string | null>(null);

  useEffect(() => {
    apiClient.getPortalConfig().then((cfg) => setRancherUiUrl(cfg.rancherUiUrl)).catch(() => {});
  }, []);
  const { isAllowed: canReadWorkflows } = usePermission('workflows', 'read');
  const { isAllowed: canManageDefaultParamsByPermission } = usePermission('workflows', 'manage_default_params');
  const canManageDefaultParams = canManageDefaultParamsByPermission || (user?.flags as any)?.is_admin === true;
  const [showDefaultParamsDialog, setShowDefaultParamsDialog] = useState(false);

  // Only users with tenants:view_costs on this page are subscribers to costs cache
  useActivityHeartbeat('cache:costs', canViewCosts);

  // Fetch tenant metadata only (no cost data)
  const fetchTenantMetadata = async () => {
    try {
      const response = await apiClient.getTenants();
      const tenantsData = response.tenants || [];
      // Show all tenants the API returns (already RBAC-filtered). Do not apply the
      // header tenant selector here — it would hide newly created tenants.
      setTenants(tenantsData);
      return tenantsData;
    } catch (err: any) {
      console.error('Failed to fetch tenant metadata:', err);
      throw err;
    }
  };

  // Fetch cost data for all tenants
  const fetchTenantCosts = async (tenantsData: Tenant[]) => {
    if (!canViewCosts) {
      setTenantCosts({});
      return;
    }
    const now = new Date();
    const { start: firstOfMonth, end: today } = getUtcMonthToDateRange(now);
    const lastDayOfMonth = getUtcLastInstantOfCalendarMonth(now);
    
    const costs: Record<string, TenantCost> = {};
    
    await Promise.all(
      tenantsData.map(async (tenant: Tenant) => {
        // Skip cost fetching for invalid tenants
        if (tenant.validation_status === 'invalid') {
          costs[tenant.id] = {
            costMonthToDate: 0,
            costProjectedMonthEnd: 0,
          };
          return;
        }
        
        try {
          console.log(`Fetching costs for tenant ${tenant.code} (${tenant.name})`, {
            firstOfMonth: firstOfMonth.toISOString(),
            today: today.toISOString(),
          });
          const costData = await apiClient.getCosts(tenant.code, undefined, firstOfMonth, today);
          console.log(`Cost data for ${tenant.code}:`, costData);
          console.log(`Cost data totalCost for ${tenant.code}:`, costData?.totalCost);
          const costMTD = costData?.totalCost ?? 0;
          console.log(`Calculated costMTD for ${tenant.code}:`, costMTD);
          
          // Use calculatedAt timestamp from the cost data for accurate projection
          const calculatedAt = costData?.calculatedAt ? new Date(costData.calculatedAt) : now;
          const minutesElapsed = (calculatedAt.getTime() - firstOfMonth.getTime()) / (1000 * 60);
          const totalMinutesInMonth = (lastDayOfMonth.getTime() - firstOfMonth.getTime()) / (1000 * 60);
          const minutesRemaining = totalMinutesInMonth - minutesElapsed;
          
          const perMinuteRate = minutesElapsed > 0 ? costMTD / minutesElapsed : 0;
          const projectedMonthEnd = costMTD + (perMinuteRate * minutesRemaining);
          
          costs[tenant.id] = {
            costMonthToDate: costMTD,
            costProjectedMonthEnd: projectedMonthEnd,
          };
          console.log(`Set costs for ${tenant.code}:`, costs[tenant.id]);
        } catch (err: any) {
          console.error(`Failed to fetch costs for tenant ${tenant.code}:`, err);
          costs[tenant.id] = {
            costMonthToDate: 0,
            costProjectedMonthEnd: 0,
          };
        }
      })
    );
    
    setTenantCosts(costs);
  };

  // Fetch both metadata and costs (for initial load)
  const fetchTenants = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const filteredTenants = await fetchTenantMetadata();
      await fetchTenantCosts(filteredTenants);
    } catch (err: any) {
      console.error('Failed to fetch tenants:', err);
      setError(err.message || 'Failed to load tenants');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTenants();
  }, [canViewCosts]);

  useEffect(() => {
    // Desktop-only actions (use Tailwind's md breakpoint)
    const mq = window.matchMedia('(min-width: 768px)');
    const update = () => setIsDesktop(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  // Live refresh when caches update
  useCacheEvents({
    enabled: true,
    cacheNames: ['tenants'],
    onEvent: () => {
      // metadata refresh is cheap; costs refresh is permission-gated inside fetchTenantCosts
      fetchTenants();
    },
  });

  useCacheEvents({
    enabled: canViewCosts,
    cacheNames: ['costs'],
    onEvent: () => fetchTenants(),
  });

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Skeleton className="h-4 w-48" />
          <Skeleton className="h-10 w-24" />
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-6 w-32 mb-2" />
                <Skeleton className="h-4 w-24" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-20 w-full mb-2" />
                <Skeleton className="h-4 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <div className="text-center py-8 text-red-600 dark:text-red-400">
          Error: {error}
        </div>
      </div>
    );
  }

  const actionItems = [
    canAddTenant
      ? {
          label: 'Add Tenant',
          icon: <Plus className="w-4 h-4" />,
          onClick: () => setShowAddDialog(true),
        }
      : null,
    canEditConfiguration
      ? {
          label: 'Edit Master Config',
          icon: <Edit2 className="w-4 h-4" />,
          onClick: () => setEditingMaster(true),
        }
      : null,
    canEditConfiguration
      ? {
          label: 'Manage Vault Secrets',
          icon: <FileText className="w-4 h-4" />,
          onClick: () => setShowVaultDialog(true),
        }
      : null,
    canEditConfiguration
      ? {
          label: 'Manage Graph Domains',
          icon: <FileText className="w-4 h-4" />,
          onClick: () => setShowGraphDomainsDialog(true),
        }
      : null,
    canManageDefaultParams
      ? {
          label: 'Default Params',
          icon: <Sliders className="w-4 h-4" />,
          onClick: () => setShowDefaultParamsDialog(true),
        }
      : null,
    isDesktop && rancherUiUrl
      ? (canOpenRancherUi ? {
          label: 'Open Rancher UI',
          icon: <ExternalLink className="w-4 h-4" />,
          onClick: () => window.open(rancherUiUrl, '_blank'),
        } : null)
      : null,
  ].filter(Boolean) as any[];

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-stretch gap-4">
        <Card className="flex-1">
          <CardHeader className="pb-2 pt-3">
            <div className="flex flex-wrap sm:flex-nowrap items-center justify-between gap-2 sm:gap-4">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  Manage tenant configurations and access
                </div>
              </div>
              <div className="flex items-center gap-2 whitespace-nowrap">
                <span className="text-2xl font-bold text-blue-600 dark:text-blue-400">{tenants.length}</span>
                <span className="text-xs text-blue-600 dark:text-blue-400 font-medium">tenants</span>
              </div>
            </div>
          </CardHeader>
        </Card>
        {actionItems.length > 0 && (
          <div className="w-full sm:w-auto sm:flex sm:self-stretch">
            <DropdownMenu
              className="w-full h-full"
              trigger={
                <Button variant="outline" size="sm" className="w-full h-full text-sm py-2">
                  <Settings className="w-4 h-4 mr-2" />
                  Actions
                </Button>
              }
              items={actionItems}
            />
          </div>
        )}
      </div>

      {tenants.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-gray-500 dark:text-gray-400">
            No tenants found. Create your first tenant to get started.
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {tenants.map((tenant) => (
            <Card key={tenant.id} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-center space-x-3">
                    <TenantLogo tenant={tenant} size="md" rounded="lg" className="border border-gray-200/80 dark:border-gray-700/80" />
                    <div>
                      <CardTitle className="text-lg" style={{ color: getTenantColor(tenant) }}>
                        {tenant.name}
                      </CardTitle>
                      <CardDescription className="font-mono text-xs mt-1">
                        {tenant.code}
                      </CardDescription>
                    </div>
                  </div>
                  <div className="flex items-center space-x-1">
                    {(() => {
                      const items = [
                        canEditConfiguration
                          ? {
                              label: 'Edit Configuration',
                              icon: <Edit2 className="w-4 h-4" />,
                              onClick: () => setEditTenantCode(tenant.code),
                            }
                          : null,
                        canViewCosts
                          ? {
                              label: 'Cost Report',
                              icon: <DollarSign className="w-4 h-4" />,
                              onClick: () => setCostReportTenant({ code: tenant.code, name: tenant.name }),
                            }
                          : null,
                      ].filter(Boolean) as any[];

                      if (items.length === 0) return null;

                      return (
                        <DropdownMenu
                          trigger={
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0"
                            >
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          }
                          items={items}
                        />
                      );
                    })()}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {tenant.validation_status === 'invalid' && (
                  <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                    <div className="flex items-start gap-2">
                      <Activity className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-amber-800 dark:text-amber-200">Configuration Incomplete</p>
                        <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                          {tenant.validation_errors || 'Missing required vault secrets'}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                {tenant.description && (
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    {tenant.description}
                  </p>
                )}
                {Array.isArray(tenant.domains) && tenant.domains.length > 0 && (
                  <div className="mb-4 text-xs text-gray-500 dark:text-gray-400">
                    Domains: {tenant.domains.join(', ')}
                  </div>
                )}
                {canViewCosts && tenantCosts[tenant.id] && tenant.validation_status !== 'invalid' && (
                  <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Cost to Date</span>
                      <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                        £{tenantCosts[tenant.id].costMonthToDate.toFixed(2)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Projected Month</span>
                      <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                        £{tenantCosts[tenant.id].costProjectedMonthEnd.toFixed(2)}
                      </span>
                    </div>
                  </div>
                )}
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  Created {formatDate(tenant.created_at)}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Cost Report Dialog */}
      {canViewCosts && costReportTenant && (
        <CostReportDialog
          open={!!costReportTenant}
          onClose={() => setCostReportTenant(null)}
          tenantCode={costReportTenant.code}
          tenantName={costReportTenant.name}
        />
      )}

      {/* Tenant Edit Dialog */}
      <TenantEditDialog
        tenantCode={editTenantCode}
        onClose={() => {
          setEditTenantCode(null);
          setEditingMaster(false);
        }}
        onSuccess={() => {
          // Only refresh tenant metadata, not costs (costs are cached for 24 hours)
          fetchTenantMetadata();
          setEditTenantCode(null);
          setEditingMaster(false);
        }}
        isMaster={editingMaster}
      />

      {/* Add Tenant Dialog */}
      <AddTenantDialog
        open={showAddDialog}
        onClose={() => setShowAddDialog(false)}
        onSuccess={() => {
          // Only refresh tenant metadata, not costs (costs are cached for 24 hours)
          fetchTenantMetadata();
          setShowAddDialog(false);
        }}
      />

      {/* Vault Secret Dialog */}
      <VaultSecretDialog
        open={showVaultDialog}
        onClose={() => setShowVaultDialog(false)}
        onSuccess={() => {
          setShowVaultDialog(false);
        }}
      />

      <MicrosoftGraphDomainsDialog
        open={showGraphDomainsDialog}
        onClose={() => setShowGraphDomainsDialog(false)}
      />

      <DefaultParamsDialog
        open={showDefaultParamsDialog}
        onClose={() => setShowDefaultParamsDialog(false)}
      />
    </div>
  );
}
